import type { NotificationFormat } from "@/lib/notificationPrefs"

const NOTIFICATION_CONFIG = {
    autoCloseMs: 15_000,
    minIntervalMs: 60_000,
    tag: "copilot-recommendation",
} as const

let lastShownAt = 0
let pending: { rec: NotificationRecommendation; opts: { format: NotificationFormat } } | null = null
let pendingTimerId: number | null = null
let welcomeShownForSession = false
let skipNextRecommendationInterval = false

type Priority = "high" | "medium" | "low"

interface NotificationRecommendation {
    priority: Priority
    text: string
    suggested_phrase?: string
}

const PRIORITY_EMOJI: Record<Priority, string> = {
    high: "\u{1F534}",
    medium: "\u{1F7E0}",
    low: "\u{1F7E2}",
}

function priorityColor(priority: Priority): string {
    if (typeof document === "undefined") {
        const fallback: Record<Priority, string> = {
            high: "#e24343",
            medium: "#e7a516",
            low: "#42ad3f",
        }
        return fallback[priority]
    }
    const css = getComputedStyle(document.documentElement)
    const map: Record<Priority, string> = {
        high: "--color-state-negative",
        medium: "--color-state-warning",
        low: "--color-state-positive",
    }
    return css.getPropertyValue(map[priority]).trim() || "#e24343"
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
    if (!("Notification" in window)) return "denied"
    if (Notification.permission !== "default") return Notification.permission
    return await new Promise<NotificationPermission>((resolve) => {
        try {
            const result = Notification.requestPermission((perm) => resolve(perm))
            if (result && typeof result.then === "function") {
                result.then(resolve).catch(() => resolve(Notification.permission))
            }
        } catch {
            resolve(Notification.permission)
        }
    })
}

function coloredIcon(priority: Priority): string {
    try {
        const size = 64
        const canvas = document.createElement("canvas")
        canvas.width = size
        canvas.height = size
        const ctx = canvas.getContext("2d")
        if (!ctx) return "/logo.svg"
        ctx.fillStyle = priorityColor(priority)
        ctx.beginPath()
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
        ctx.fill()
        return canvas.toDataURL("image/png")
    } catch {
        return "/logo.svg"
    }
}

function canShowRecommendationNotification(): boolean {
    if (!("Notification" in window)) return false
    if (Notification.permission !== "granted") return false
    if (document.hasFocus()) return false
    return true
}

function clearPendingNotification(): void {
    if (pendingTimerId !== null) {
        window.clearTimeout(pendingTimerId)
        pendingTimerId = null
    }
    pending = null
}

export function resetRecommendationNotificationQueue(): void {
    clearPendingNotification()
    lastShownAt = 0
    welcomeShownForSession = false
    skipNextRecommendationInterval = false
}

export function showWelcomeNotification(): void {
    if (welcomeShownForSession) return
    if (!("Notification" in window)) return
    if (Notification.permission !== "granted") return
    if (document.hasFocus()) return

    welcomeShownForSession = true
    skipNextRecommendationInterval = true

    const notification = new Notification("\u{1F399}\uFE0F Copilot в эфире", {
        body: "Запись продолжается. Подсказки придут сюда, когда появятся.",
        tag: `${NOTIFICATION_CONFIG.tag}-welcome-${Date.now()}`,
        icon: coloredIcon("low"),
    })
    notification.onclick = () => {
        window.focus()
        notification.close()
    }
    window.setTimeout(() => notification.close(), NOTIFICATION_CONFIG.autoCloseMs)
}

function displayNotification(
    rec: NotificationRecommendation,
    opts: { format: NotificationFormat },
): void {
    const title = `${PRIORITY_EMOJI[rec.priority]} Подсказка`
    let body = ""
    if (opts.format === "color_text") {
        body = rec.text
        if (rec.suggested_phrase) body += `\n\u{1F4AC} ${rec.suggested_phrase}`
    }

    const notification = new Notification(title, {
        body,
        tag: `${NOTIFICATION_CONFIG.tag}-${Date.now()}`,
        icon: coloredIcon(rec.priority),
    })

    notification.onclick = () => {
        window.focus()
        notification.close()
    }

    window.setTimeout(() => notification.close(), NOTIFICATION_CONFIG.autoCloseMs)
}

function flushPendingNotification(): void {
    pendingTimerId = null
    const queued = pending
    pending = null
    if (!queued || !canShowRecommendationNotification()) return

    displayNotification(queued.rec, queued.opts)
    lastShownAt = Date.now()
}

export function showRecommendationNotification(
    rec: NotificationRecommendation,
    opts: { format: NotificationFormat },
): void {
    if (!canShowRecommendationNotification()) return

    if (skipNextRecommendationInterval) {
        skipNextRecommendationInterval = false
        clearPendingNotification()
        displayNotification(rec, opts)
        lastShownAt = Date.now()
        return
    }

    const now = Date.now()
    const elapsed = lastShownAt === 0 ? NOTIFICATION_CONFIG.minIntervalMs : now - lastShownAt

    if (lastShownAt === 0 || elapsed >= NOTIFICATION_CONFIG.minIntervalMs) {
        clearPendingNotification()
        displayNotification(rec, opts)
        lastShownAt = now
        return
    }

    pending = { rec, opts }
    if (pendingTimerId !== null) return

    pendingTimerId = window.setTimeout(
        flushPendingNotification,
        NOTIFICATION_CONFIG.minIntervalMs - elapsed,
    )
}
